import { useMemo } from "react";
import { StockData } from "../store";
import { getMaxValue, getMinValue, getTranslateData, LOCALES } from "../utils";
import { formatNumberDecimals } from "../components/StockDetails/utils/formatNumberDecimals";
import { ohlcvUtcTime } from "../components/DetailsCommon/Forecast/utils/ohlcvUtcTime";
import { useStockData } from "./useSubscribStock";
import { useStockDetailsMarket } from "./useStockDetailsMarket";
import { genForecastLineData, getCurrencySymbol } from "@utils/stock";

type IUseStockDetailsHandleProps = {
  tokenList?: ReturnType<typeof useStockDetailsMarket>["data"];
  symbol?: string;
  locale?: string;
};

export const useStockDetailsHandle = (props: IUseStockDetailsHandleProps) => {
  const { tokenList: stockData, locale = LOCALES.EN } = props;
  const {
    symbol,
    id,
    name,
    logo,
    description: desc,
    multi_language_desc,
    trading_data,
    company_name,
    recommendation_trend,
    target_price,
    ohlcv,
    industry,
    sector,
    executives,
    dividends,
    currency,
  } = stockData || {};

  const { data: socketData }: { data: StockData } = useStockData(symbol || "");

  const freshCurrentPrice = useMemo(() => {
    return socketData?.p || trading_data?.current_price;
  }, [socketData?.p, trading_data?.current_price]);

  const freshCurrentVolume = useMemo(() => {
    return socketData?.tv || trading_data?.volume;
  }, [socketData?.tv, trading_data?.volume]);

  const {
    current_price,
    previous_close: prevClose,
    high,
    low,
    open,
    close_2_days_ago,
    close_5_days_ago,
    close_10_days_ago,
    low_52_w,
    high_52_w,
    eps_ttm,
    shares_outstanding,
    average_volume_3_m,
  } = trading_data || {};

  const { dayRange, weekRange52, marketCap, dividendYield, volume } =
    useMemo(() => {
      const dayRangeLow = getMinValue(low!, freshCurrentPrice as number);
      const dayRangeHigh = getMaxValue(high!, freshCurrentPrice as number);
      const formatDayRange = `${dayRangeLow ? `${getCurrencySymbol(currency)}${formatNumberDecimals(dayRangeLow)}` : ""} - ${dayRangeHigh ? `${getCurrencySymbol(currency)}${formatNumberDecimals(dayRangeHigh)}` : ""}`;
      const weekRange52Low = getMinValue(
        low_52_w!,
        freshCurrentPrice as number,
      );
      const weekRange52High = getMaxValue(
        high_52_w!,
        freshCurrentPrice as number,
      );
      const formatWeekRange52 = `${weekRange52Low ? `${getCurrencySymbol(currency)}${formatNumberDecimals(weekRange52Low)}` : ""} - ${weekRange52High ? `${getCurrencySymbol(currency)}${formatNumberDecimals(weekRange52High)}` : ""}`;
      return {
        dayRange: formatDayRange, //根据当前价格比较，实时更新
        weekRange52: formatWeekRange52, //根据当前价格比较，实时更新
        marketCap:
          (freshCurrentPrice as number) * (shares_outstanding as number), //根据当前价格比较，实时更新 *总发行量
        volume: freshCurrentVolume, // socket会推送
        dividendYield: dividends?.dividend_yield_ttm, //根据当前价格比较，实时更新
      };
    }, [
      low,
      high,
      low_52_w,
      high_52_w,
      freshCurrentPrice,
      freshCurrentVolume,
      current_price,
      dividends?.dividend_yield_ttm,
      currency,
    ]);

  const forecastLineData = useMemo(() => {
    if (Array.isArray(ohlcv) && ohlcv.length > 0) {
      const { XAxis, YAxisData, ticks } = genForecastLineData(ohlcv as any, {
        handleXLabel: (date) => {
          const nyDate = date.tz("America/New_York");
          const offsetInHours = nyDate.utcOffset() / 60;
          return nyDate.format("MMM DD YYYY") + " GMT" + offsetInHours;
        },
        handleTickLabel: (date) => ohlcvUtcTime(date).format("MMM DD YY"),
      });
      return { XAxis, YAxis: YAxisData, ticks };
    }
    return { XAxis: [], YAxis: [], ticks: [] };
  }, [ohlcv]);

  const language_desc = getTranslateData({
    locale,
    languageData: multi_language_desc as { [key: string]: string },
  });

  return {
    ApiCurrentPrice: current_price,
    currentPrice: freshCurrentPrice,
    desc,
    language_desc,
    logo,
    name,
    symbol: symbol,
    id,

    company_name,
    trading_data,
    recommendation_trend,
    target_price,
    forecastLineData,

    industry,
    sector,
    executives,

    prevClose,
    open,
    high,
    low,
    dayRange,
    weekRange52,
    marketCap,
    volume,
    averageVolume3m: average_volume_3_m,
    epsTTM: eps_ttm,
    dividendYield: dividendYield,

    close_2_days_ago,
    close_5_days_ago,
    close_10_days_ago,
    low_52_w,
    high_52_w,
    ohlcv,
    themeIds: stockData?.theme_ids,
    targetPrice: target_price,
    recommendationTrend: recommendation_trend,
    rateData: {
      rate: stockData?.latest_rating?.level?.final,
      id: stockData?.id,
      symbol: stockData?.symbol,
    },
  };
};
